import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import AttachmentIcon from "../../../assets/attachment.svg";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBackRounded, DownloadOutlined, FamilyRestroomRounded } from "@mui/icons-material";
import MainLayout from "../../../layouts/main/main_layout";
import PdfIcon from "../../../assets/pdf_icon.svg";
import XlsIcon from "../../../assets/xls_icon.svg";
import { useQuery } from "react-query";
import {
  downloadEnquiry,
  getEnquiriesById,
} from "../../../services/schemes/schemes";
import { downloadFromBlob } from "../../../utils/utils";
import Loading from "../../../components/loading/loading";
import { black, greyEnabled, orangeLightActive, greenLight, purple, purpleLight } from "../../../theme/colors";
import SendEnquiryNotification from "../enquiry_details/components/send_enquiry_notification";
import { useFeedback } from "../../../providers/feedback/feeedback";

export default function FamilyEnquiryDetails() {
  let { id } = useParams();
  let navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [downloadingFormat, setDownloadingFormat] = useState<"pdf" | "excel" | null>(null);

  const { data: schemeDetails, isLoading } = useQuery(
    ["familyEnquiry", id],
    async () => {
      let payload = {
        type: "family",
      };
      let response = await getEnquiriesById(id!, payload);
      return response.data.data;
    },
    {
      enabled: !!id,
      retry: false,
    }
  );

  const sentNotification = schemeDetails?.notifications?.find(
    (notification: any) => notification.status === "Success"
  );

  const downloadEnquiryApiCall = (format: "pdf" | "excel") => {
    setDownloadingFormat(format);
    downloadEnquiry(id!, format, "family")
      .then((res) => {
        let extension = format === "pdf" ? ".pdf" : ".xlsx";
        downloadFromBlob(res, extension);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Something went wrong while generating the report."
        );
      })
      .finally(() => setDownloadingFormat(null));
  };

  function divider() {
    return (
      <Divider
        sx={{
          display: "flex",
          opacity: 0.5,
          borderBottom: "1px dotted #7C797C",
        }}
      />
    );
  }

  function fieldItem({ title, value }: { title: string; value?: string }) {
    return (
      <Box style={{ overflow: "hidden" }}>
        <Typography
          variant="body2"
          sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ color: black, fontWeight: 500, mt: 0.25 }}
          textOverflow={"ellipsis"}
          overflow="hidden"
        >
          {value || "-"}
        </Typography>
      </Box>
    );
  }

  function reportIconButton({
    imgSrc,
    backgroundColor,
    tooltip,
    loading,
    onTap,
  }: {
    imgSrc: string;
    backgroundColor: string;
    tooltip: string;
    loading: boolean;
    onTap: () => void;
  }) {
    return (
      <Box
        sx={{
          display: "flex",
          backgroundColor,
          justifyContent: "center",
          alignItems: "center",
          p: 1,
          borderRadius: 3,
          ml: 2,
          opacity: downloadingFormat && !loading ? 0.6 : 1,
        }}
      >
        <IconButton
          onClick={onTap}
          disabled={!!downloadingFormat}
          title={tooltip}
          sx={{
            height: 24,
            width: 24,
          }}
        >
          {loading ? (
            <CircularProgress size={18} sx={{ color: purple }} />
          ) : (
            <img src={imgSrc} alt={tooltip} />
          )}
        </IconButton>
      </Box>
    );
  }

  return (
    <MainLayout>
      <Grid container mt={4}>
        <Grid item lg={8} md={12} sm={12} xs={12} xl={8}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackRounded />
            </IconButton>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: purpleLight,
                  flexShrink: 0,
                }}
              >
                <FamilyRestroomRounded sx={{ color: purple, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color={black}>
                  Family Enquiry Details
                </Typography>
                <Typography variant="body2" color={greyEnabled}>
                  {schemeDetails?.userName || "View family enquiry details."}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Card
            elevation={0}
            sx={{ width: "100%", mt: 2, borderRadius: 3, border: "1px solid #ECECEC" }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <Loading />
                </Box>
              ) : (
                <Box overflow="auto" flex={1}>
                  <Grid container spacing={3}>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Reference number",
                        value: schemeDetails?.enquiryId,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Primary user full name",
                        value: schemeDetails?.userName,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Primary user contact number",
                        value: schemeDetails?.mobileNumber,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Relationship with primary user",
                        value: schemeDetails?.relationship,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Family member name",
                        value: schemeDetails?.memberName,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Family member date of birth",
                        value: schemeDetails?.memberDob,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Family member gender",
                        value: schemeDetails?.memberGender,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Family member occupation",
                        value: schemeDetails?.memberOccupation,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Family member contact number",
                        value: schemeDetails?.memberMobileNumber,
                      })}
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      {divider()}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Scheme enquired",
                        value: schemeDetails?.name,
                      })}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {fieldItem({
                        title: "Government",
                        value: schemeDetails?.governmentType,
                      })}
                    </Grid>

                    <Grid item md={12} sm={12}>
                      <Typography
                        variant="body2"
                        sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
                      >
                        Supporting Documents
                      </Typography>
                      <List dense={true}>
                        {schemeDetails?.uploadedDocuments?.map(
                          (
                            document: { file: string; fileName: string },
                            index: any
                          ) => {
                            return (
                              <ListItem
                                key={index}
                                sx={{
                                  border: `1px solid ${purpleLight}`,
                                  borderRadius: 4,
                                  mb: 1,
                                }}
                              >
                                <ListItemIcon>
                                  <img src={AttachmentIcon} alt="" />
                                </ListItemIcon>
                                <ListItemText primary={"Document"}></ListItemText>
                                <ListItemIcon>
                                  <a
                                    href={"data:image/png;base64," + document?.file}
                                    target="_blank"
                                    download={document.fileName}
                                    rel="noreferrer"
                                  >
                                    <IconButton color="primary">
                                      <DownloadOutlined />
                                    </IconButton>
                                  </a>
                                </ListItemIcon>
                              </ListItem>
                            );
                          }
                        )}
                      </List>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        sx={{ p: 0, textTransform: "none", fontWeight: 600, color: purple }}
                        variant="text"
                        onClick={() => navigate("/schemes")}
                      >
                        View All Scheme details
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
                      >
                        Categories
                      </Typography>
                      <Box display={"flex"} flexDirection={"row"} flexWrap={"wrap"} mt={0.5}>
                        {schemeDetails?.categories?.map((item: any) => {
                          return (
                            <Box
                              key={item}
                              display={"flex"}
                              flexDirection={"row"}
                              mr={1}
                              p={1}
                              borderRadius={3}
                              sx={{ backgroundColor: purpleLight }}
                              mb={1}
                            >
                              <Typography variant="body2" color={purple} fontWeight={600}>
                                {item}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {!isLoading && (
                <>
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    mt={3}
                    alignItems={"center"}
                  >
                    <Typography variant="subtitle2">Generate Report: </Typography>
                    {reportIconButton({
                      imgSrc: PdfIcon,
                      backgroundColor: orangeLightActive,
                      tooltip: "Download as PDF",
                      loading: downloadingFormat === "pdf",
                      onTap: () => downloadEnquiryApiCall("pdf"),
                    })}
                    {reportIconButton({
                      imgSrc: XlsIcon,
                      backgroundColor: greenLight,
                      tooltip: "Download as XLS",
                      loading: downloadingFormat === "excel",
                      onTap: () => downloadEnquiryApiCall("excel"),
                    })}
                  </Box>
                  <Box mt={3}>
                    <Button
                      onClick={() => navigate(-1)}
                      variant="outlined"
                      sx={{
                        borderColor: purple,
                        color: purple,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": { borderColor: purple, bgcolor: purpleLight },
                      }}
                    >
                      Back To Family Enquiries
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        {!isLoading && (
          <Grid item xs={12}>
            <SendEnquiryNotification
              enquiryId={id!}
              enquiryType="family"
              sentNotification={sentNotification}
            />
          </Grid>
        )}
      </Grid>
    </MainLayout>
  );
}
